import { AutofillMonitor } from '@angular/cdk/text-field';
import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    forwardRef,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Color } from '../../../constants/color.constants';
import { DomEvents } from '../../../constants/dom-events.constants';
import { KeyCodes } from '../../../constants/keycodes.constants';
import { Regex } from '../../../constants/regex.constants';
import { DetectBrowserService } from '../../../services/detect-browser/detect-browser.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';
import { BaseFormControlComponent } from '../base-form-control.component';

/**
 * @group Forms
 * @component Input
 *
 * @export
 * @class LsqInputComponent
 * @extends {BaseFormControlComponent<string>}
 * @implements {AfterContentInit, AfterViewInit}
 * @property {FormControl} [control] Add the control to add unique validation messages. Either the control or group must be present for custom messages.
 * @property {FormGroup} [group] Add the group to add unique validation messages. Either the control or group must be present for custom messages.
 * @property {string[]} [prependIcon] Defines an `input-group-prepend` with an icon (e.g. a dollar sign to denote a currency input). `prependIcon[0]` should define the icon type; `prependIcon[1]` should define the icon color (defaults to black).
 * * @property {string[]} [appendIcon] Defines an `input-group-prepend` with an icon (e.g. a dollar sign to denote a currency input). `appendIcon[0]` should define the icon type; `appendIcon[1]` should define the icon color (defaults to black).
 * @property {Function} [textFormatter] Formatting function to be applied to all content input by the user
 * @property {Function} [handleKeypress] Event handler to be passed down to the input element
 */
@LifecycleAware()
@Component({
    selector: 'lsq-input',
    template: `
        <ng-template #coreInput>
            <div
                class="form-group {{ parentClass }}"
                [ngClass]="{
                    'has-basic-label': basicLabel,
                    'has-float-label': floatLabel,
                    'has-inner-label': !floatLabel && label && innerLabel,
                    invalid: showErrorMessage(shouldValidate())
                }"
                (click)="setFocus(true)"
            >
                <!-- INPUT LABEL -->
                <ng-container *ngIf="inputLabel">
                    <ng-container *ngTemplateOutlet="inputLabel"> </ng-container>
                </ng-container>

                <ng-container *ngIf="!inputLabel">
                    <label
                        *ngIf="label && !floatLabel && !innerLabel"
                        [for]="name"
                        class="{{ labelClass }} "
                        [ngClass]="{
                            'float-label': floatLabel,
                            'inner-label': !this.floatLabel && this.label && this.innerLabel,
                            'basic-label': basicLabel,
                            'text-danger': showErrorMessage(shouldValidate())
                        }"
                    >
                        {{ label }}
                    </label>
                </ng-container>

                <div
                    class="input-group {{ inputGroupClass }}"
                    [ngClass]="{
                        'is-focused': isFocused,
                        'is-invalid': showErrorMessage(shouldValidate()),
                        disabled: disabled,
                        'has-addon-prepended': prependIcon,
                        'has-addon-appended': type === 'password' || appendIcon,
                        'is-focused-or-filled': isFocusedOrFilled(),
                        'hide-number-spinner': hideButtonSpinner
                    }"
                >
                    <!-- INPUT PREPEND -->
                    <ng-container *ngIf="inputGroupPrepend">
                        <ng-container *ngTemplateOutlet="inputGroupPrepend"> </ng-container>
                    </ng-container>

                    <ng-container *ngIf="!inputGroupPrepend">
                        <span *ngIf="prependIcon" class="input-group-prepend">
                            <lsq-icon
                                [type]="prependIcon[0]"
                                [color]="
                                    !showErrorMessage(shouldValidate()) ? prependIcon[1] : color.Red
                                "
                            >
                            </lsq-icon>
                        </span>
                    </ng-container>

                    <!-- INPUT  -->
                    <ng-container *ngIf="inputViewport">
                        <ng-container *ngTemplateOutlet="inputViewport"> </ng-container>
                    </ng-container>

                    <ng-container *ngIf="!inputViewport && !hideInputElement">
                        <input
                            *ngIf="!this.usesNgxMask"
                            #inputElement
                            [ngClass]="inputClass"
                            [id]="name"
                            [attr.aria-label]="
                                showErrorMessage(shouldValidate())
                                    ? ariaLabel + ' ' + getErrorMessage()
                                    : ariaLabel
                            "
                            [type]="calculatedType"
                            [placeholder]="placeholder ? placeholder : ' '"
                            [ngModel]="inputValue ? inputValue : model"
                            [attr.max]="max"
                            [attr.min]="min"
                            [maxlength]="maxLength"
                            [attr.readonly]="readOnly || undefined"
                            [attr.tabindex]="tabindex"
                            [disabled]="disabled"
                            [pattern]="pattern"
                            [autocomplete]="autoComplete"
                            [textMask]="{ mask: getMask(mask), guide: false }"
                            [ngModelOptions]="ngModelOptions"
                            (focus)="handleFocusChange(true)"
                            (blur)="handleFocusChange(false)"
                            (input)="handleInput($event)"
                            (keydown)="handleKeydown($event)"
                            (keypress)="handleInternalKeyPress($event)"
                            (ngModelChange)="handleValueChange($event)"
                            (paste)="type === 'password' ? trimPaste($event) : undefined"
                            (click)="unfreezeInput($event)"
                        />
                        <input
                            *ngIf="this.usesNgxMask"
                            #inputElement
                            [mask]="this.mask"
                            [ngClass]="inputClass"
                            [id]="name"
                            [attr.aria-label]="
                                showErrorMessage(shouldValidate())
                                    ? ariaLabel + ' ' + getErrorMessage()
                                    : ariaLabel
                            "
                            [type]="calculatedType"
                            [placeholder]="placeholder ? placeholder : ' '"
                            [ngModel]="inputValue ? inputValue : model"
                            [attr.max]="max"
                            [attr.min]="min"
                            [maxlength]="maxLength"
                            [attr.readonly]="readOnly || undefined"
                            [attr.tabindex]="tabindex"
                            [disabled]="disabled"
                            [pattern]="pattern"
                            [autocomplete]="autoComplete"
                            [ngModelOptions]="ngModelOptions"
                            (focus)="handleFocusChange(true)"
                            (blur)="handleFocusChange(false)"
                            (input)="handleInput($event)"
                            (keydown)="handleKeydown($event)"
                            (keypress)="handleInternalKeyPress($event)"
                            (ngModelChange)="handleValueChange($event)"
                            (paste)="type === 'password' ? trimPaste($event) : undefined"
                            (click)="unfreezeInput($event)"
                        />
                    </ng-container>

                    <ng-container *ngIf="inputGroupAppend">
                        <ng-container *ngTemplateOutlet="inputGroupAppend"> </ng-container>
                    </ng-container>
                    <ng-container
                        *ngIf="
                            !inputGroupAppend &&
                            showPasswordVisibilityToggle &&
                            type === 'password' &&
                            !!model?.length
                        "
                    >
                        <lsq-button
                            appearance="transparent"
                            [ngClass]="{ 'has-basic-label': basicLabel }"
                            [ngClass]="{ 'has-inner-label': innerLabel }"
                            (buttonClick)="togglePasswordShown()"
                        >
                            <ng-container *ngIf="calculatedType === 'password'">Show</ng-container>
                            <ng-container *ngIf="calculatedType !== 'password'">Hide</ng-container>
                        </lsq-button>
                    </ng-container>
                    <ng-container *ngIf="!inputGroupAppend">
                        <span *ngIf="appendIcon" class="input-group-append">
                            <lsq-icon
                                [type]="appendIcon[0]"
                                [height]="basicLabel ? 15 : 20"
                                [color]="
                                    !showErrorMessage(shouldValidate()) ? appendIcon[1] : color.Red
                                "
                            >
                            </lsq-icon>
                        </span>
                    </ng-container>

                    <ng-content></ng-content>
                </div>

                <label
                    *ngIf="label && (floatLabel || innerLabel)"
                    [for]="name"
                    [ngClass]="{
                        'float-label': floatLabel,
                        'inner-label': !this.floatLabel && this.label && this.innerLabel,
                        'basic-label': basicLabel,
                        'text-danger': showErrorMessage(shouldValidate())
                    }"
                >
                    {{ label }}
                </label>
            </div>

            <ng-container *ngIf="dropdownViewport">
                <ng-container *ngTemplateOutlet="dropdownViewport"> </ng-container>
            </ng-container>
        </ng-template>

        <div
            class="lsq-input"
            [id]="name + 'InputComponent'"
            [ngClass]="[
                containerClass ? containerClass : '',
                basicLabel ? 'basic-label-input' : ''
            ]"
        >
            <ng-container *ngTemplateOutlet="coreInput"> </ng-container>

            <h6
                class="input-error-message text-uppercase font-weight-bold"
                *ngIf="showErrorMessage(shouldValidate())"
            >
                {{ getErrorMessage() }}
            </h6>
        </div>
    `,
    styleUrls: ['./lsq-input.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqInputComponent),
            multi: true
        }
    ]
})
export class LsqInputComponent extends BaseFormControlComponent<string>
    implements OnInit, AfterContentInit, AfterViewInit, OnDestroy {
    @ViewChild('inputElement') public inputElement: ElementRef;
    @Input() public disabled = false;
    @Input() public name: string;
    @Input() public label: string;
    @Input() public inputValue?: string;
    @Input() public basicLabel = false;
    @Input() public floatLabel = false;
    @Input() public innerLabel = this.basicLabel ? false : true;
    @Input() public type = 'text';
    @Input() public placeholder?: string;
    @Input() public maxLength?: string;
    @Input() public minLength?: string;
    @Input() public min?: number;
    @Input() public max?: number;
    @Input() public readOnly = false;
    @Input() public parentClass?: string;
    @Input() public containerClass?: string;
    @Input() public inputGroupClass = '';
    @Input() public inputClass?: string;
    @Input() public labelClass?: string;
    @Input() public prependIcon?: string[];
    @Input() public appendIcon?: string[];
    @Input() public hideButtonSpinner = true;
    @Input() public textFormatter?: (args?: any) => any;
    @Input() public mask?: Regex = null;
    @Input() public unmask?: RegExp = null;
    @Input() public digitsOnly = false;
    @Input() public autoFocus?: boolean;
    @Input() public autoComplete = 'new-password'; // 'new-password' ensures no autocomplete is shown in all browsers.
    @Input() public noPaste = false;
    @Input() public showPasswordVisibilityToggle = true;
    @Input() public tabindex?: string;
    @Input() public hideInputElement = false;
    @Input() public preventControlUpdate = false;
    @Input() public ariaLabel?: string;
    @Input() public pattern?: string = '';
    @Input() public usesNgxMask?: boolean = false;

    @Output() public focus = new EventEmitter();
    @Output() public blur = new EventEmitter();
    @Output() public keyDownEvent = new EventEmitter();
    @Output() public inputValueChanged = new EventEmitter<string>();
    @Output() public modelChanged = new EventEmitter<string>();

    @ContentChild('inputLabel', { static: true }) public inputLabel: TemplateRef<any>;
    @ContentChild('inputGroupPrepend', { static: true }) public inputGroupPrepend: TemplateRef<any>;
    @ContentChild('inputViewport', { static: true }) public inputViewport: TemplateRef<any>;
    @ContentChild('inputGroupAppend', { static: true }) public inputGroupAppend: TemplateRef<any>;
    @ContentChild('dropdownViewport', { static: true }) public dropdownViewport: TemplateRef<any>;

    public ngModelOptions = { updateOn: DomEvents.Change };
    public color = Color;
    public detectBrowserService: DetectBrowserService;
    public isFocused = false;
    public isAutoFilled = false;
    // The control's `touched` property should track this according to its documentation,
    // but it is going truthy as soon as the model changes.
    public hasInitialBlurHappened = false;
    public calculatedType: string;

    private _autofillMonitor: AutofillMonitor;

    constructor(public injector: Injector) {
        super(injector);
        this.detectBrowserService = this.injector.get(DetectBrowserService);
        this._autofillMonitor = this.injector.get(AutofillMonitor);
    }

    public ngOnInit(): void {
        // When working with numbers, must treat as text for setting char length.
        if (this.type === 'number' && this.maxLength) {
            this.calculatedType = 'text';
            this.pattern = 'd*';
        } else {
            this.calculatedType = this.type;
        }

        if (!this.ariaLabel) {
            this.ariaLabel = this.label;
        }
    }

    public ngAfterContentInit(): void {
        if (this.basicLabel) {
            this.floatLabel = false;
            this.innerLabel = false;
        }
        this.inputClass = this.inputClass ? `form-control ${this.inputClass}` : 'form-control';
    }

    public ngAfterViewInit(): void {
        this.fixPrematurelyDirtiedInputs();
        if (this.autoFocus) {
            Utilities.executeAfterRender(() => {
                this.handleFocusChange(true);
                this.inputElement.nativeElement.focus();
            });
        }
        if (this.noPaste) {
            this.inputElement.nativeElement.onpaste = (event: Event) => {
                event.preventDefault();
                return false;
            };
        }

        // This marks the field as autofilled so we can style it correctly.
        if (!this.inputViewport && !this.hideInputElement) {
            this._autofillMonitor
                .monitor(this.inputElement.nativeElement)
                .pipe(forLifeOf(this))
                .subscribe((event) => {
                    this.isAutoFilled = event.isAutofilled;
                });
        }
    }

    public ngOnDestroy(): void {
        if (!!this.inputElement) {
            this._autofillMonitor.stopMonitoring(this.inputElement.nativeElement);
        }
        super.ngOnDestroy();
    }

    // Chrome has a bug that locks an input when it autofills. This prevents that.
    // https://stackoverflow.com/questions/58201291/chrome-autocomplete-lock-inputs-like-they-are-not-clickable
    public unfreezeInput(ev: MouseEvent): void {
        (ev.target as HTMLInputElement).value = (ev.target as HTMLInputElement).value;
    }

    // Mask plugin does not handle null well so we have to pass it like this so it ignores the mask.
    public getMask(mask: Regex): Regex | false {
        return !mask ? false : mask;
    }

    @Input()
    public handleKeypress(_?: KeyboardEvent): void {}

    public handleKeydown(event: KeyboardEvent): void {
        this.keyDownEvent.emit(event);
    }

    public handleInput(e?: KeyboardEvent): void {
        this.inputValueChanged.emit(this.inputElement.nativeElement.value);
    }

    public handleFocusChange(isFocused: boolean): void {
        this.isAutoFilled = false;
        this.isFocused = isFocused;
        if (this.isFocused) {
            this.focus.emit();
        } else {
            if (this.textFormatter) {
                this.handleChange(this.textFormatter(this.model));
            }
            this.blur.emit();
            this.hasInitialBlurHappened = true;
        }
    }

    public setFocus(shouldFocus = true, shouldSelect = false): void {
        if (!!this.inputElement) {
            if (shouldFocus) {
                this.inputElement.nativeElement.focus();

                if (shouldSelect) {
                    this.inputElement.nativeElement.select();
                }
            } else {
                this.inputElement.nativeElement.blur();
            }
        }
    }

    public handleChangeCustom(value: string): void {
        this.isAutoFilled = false;
        if (this.textFormatter) {
            this.handleChange(this.textFormatter(value));
            // If the formatted text is the same as the text in the model,
            // Angular will not update as a performance optimization.
            // However, we need the value in the input to be replaced with the formatted value.
            this.inputElement.nativeElement.value = this.textFormatter(value);
        } else {
            this.handleChange(value);
        }
    }

    public handleValueChange(value: string): void {
        this.isAutoFilled = false;
        let newValue = value;
        if (this.digitsOnly) {
            newValue = newValue.replace(/[^0-9]/g, '');
        }

        //Get this value before the unmask otherwise it will always be changed.
        const valueChanged = value !== newValue;

        // Remove mask from value.
        if (this.unmask) {
            newValue = newValue.replace(this.unmask, '');
        }

        if (!this.preventControlUpdate) {
            this.handleChange(newValue);
            // This specifically fixes and issue where we edit value and it is the
            // same as the old value so angular does not not detect a change to update the inputs value.
            if (valueChanged) {
                this.inputElement.nativeElement.value = newValue;
            }
        }
    }

    public isFocusedOrFilled(): boolean {
        return (
            this.isFocused ||
            (this.model && !!this.model.length) ||
            typeof this.model === 'number' ||
            this.isAutoFilled
        );
    }

    public handleInternalKeyPress(event: KeyboardEvent): void {
        if (event.which === KeyCodes.Enter) {
            // If the user presses 'enter' force a blur for validation.
            this.inputElement.nativeElement.blur();
            return;
        }
        if (this.digitsOnly) {
            // Only allow number keys.
            if (event.which < KeyCodes.Zero || event.which > KeyCodes.Nine) {
                event.preventDefault();
                return;
            }
        }
        this.handleKeypress(event);
    }

    public shouldValidate(): boolean {
        return !this.isFocused || this.hasInitialBlurHappened;
    }

    // Called directly from FormGroup
    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public togglePasswordShown(): void {
        this.calculatedType === 'password'
            ? (this.calculatedType = 'text')
            : (this.calculatedType = 'password');
    }

    public trimPaste(event: ClipboardEvent): void {
        const str = event.clipboardData.getData('text/plain');
        this.handleChange(str.trim());
        event.preventDefault();
    }

    public clear(): void {
        this.inputElement.nativeElement.value = '';
        this.handleChange('');
    }
}
